import { useEffect, useMemo, useState } from "react";
import ReactFlow, { Background, Controls, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import api from "@/lib/api";

function CategoryNode({ data }) {
  return (
    <div className="px-4 py-3 rounded-md border border-foreground/30 bg-background text-foreground min-w-[160px] shadow-sm">
      <Handle type="source" position={Position.Bottom} className="!bg-foreground/30" />
      <p className="eyebrow mb-1">{data.type}</p>
      <p className="font-display text-sm">{data.label}</p>
      <p className="text-xs text-muted-foreground font-mono mt-1">{data.count} entries</p>
    </div>
  );
}

function KnowledgeNode({ data }) {
  return (
    <div className="px-3 py-2 rounded-md border border-border bg-card text-foreground max-w-[220px]">
      <Handle type="target" position={Position.Top} className="!bg-border" />
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{data.category}</p>
      <p className="text-xs font-medium leading-tight mt-0.5 line-clamp-2">{data.label}</p>
    </div>
  );
}

const nodeTypes = { category: CategoryNode, department: CategoryNode, knowledge: KnowledgeNode };

export default function MapPage() {
  const [raw, setRaw] = useState({ nodes: [], edges: [] });

  useEffect(() => {
    api.get("/map").then((r) => setRaw(r.data)).catch(() => {});
  }, []);

  const { nodes, edges } = useMemo(() => {
    const catNodes = raw.nodes.filter((n) => n.type === "category");
    const depNodes = raw.nodes.filter((n) => n.type === "department");
    const knNodes = raw.nodes.filter((n) => n.type === "knowledge");

    const positioned = [];
    catNodes.forEach((n, i) => positioned.push({
      id: n.id, type: "category", position: { x: 40 + i * 220, y: 20 },
      data: { label: n.label, count: n.count, type: "Category" },
    }));
    depNodes.forEach((n, i) => positioned.push({
      id: n.id, type: "department", position: { x: 40 + i * 220, y: 660 },
      data: { label: n.label, count: n.count, type: "Department" },
    }));
    // Group knowledge by category
    const byCat = {};
    knNodes.forEach((n) => { byCat[n.category] = (byCat[n.category] || []); byCat[n.category].push(n); });
    Object.entries(byCat).forEach(([cat, arr], ci) => {
      arr.forEach((n, ni) => {
        positioned.push({
          id: n.id, type: "knowledge",
          position: { x: 40 + ci * 240 + (ni % 2) * 20, y: 180 + Math.floor(ni) * 90 },
          data: { label: n.label, category: n.category, department: n.department },
        });
      });
    });
    const edg = raw.edges
      .filter((e) => e.source.startsWith("cat-"))
      .map((e, i) => ({ id: `e-${i}`, source: e.source, target: e.target, animated: false, style: { stroke: "hsl(var(--border))" } }));
    return { nodes: positioned, edges: edg };
  }, [raw]);

  return (
    <div className="p-8 max-w-6xl">
      <header className="mb-6">
        <p className="eyebrow mb-3">Knowledge Map</p>
        <h1 className="font-display text-3xl">How the company works</h1>
        <p className="mt-2 text-muted-foreground">Visual map of categories, departments, and connected knowledge.</p>
      </header>
      <div className="card-flat h-[70vh]" data-testid="map-canvas">
        {nodes.length === 0 ? (
          <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
            No knowledge yet — add some sources to see the map.
          </div>
        ) : (
          <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView proOptions={{ hideAttribution: true }}>
            <Background gap={24} size={1} color="hsl(var(--border))" />
            <Controls />
          </ReactFlow>
        )}
      </div>
    </div>
  );
}
